import { useEffect, useState } from "react";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { AdminAssistantChat } from "@/components/admin/AdminAssistantChat";
import { toast } from "sonner";

const DESCRIPTION_OPTIONS = ["Brokerage Fee", "Overtime", "Forms", "Handling and Storage", "Delivery", "Custom Entry"];
const DEFAULT_RATES = { Overtime: 1500, Forms: 600, Delivery: 6000 };
const DEFAULT_TAXABLE = { "Brokerage Fee": true, Overtime: true, Forms: true, "Handling and Storage": false, Delivery: true };

const blankLine = () => ({
  key: crypto.randomUUID(),
  description: "Brokerage Fee",
  customDescription: "",
  quantity: 1,
  rate: 0,
  taxable: true,
  file_reference: "",
});

const todayDMY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export const InvoiceForm = ({ onCreated }) => {
  const [nextNumber, setNextNumber] = useState(null);
  const [date, setDate] = useState(todayDMY());
  const [billToName, setBillToName] = useState("");
  const [billToAddress, setBillToAddress] = useState("");
  const [billToEmail, setBillToEmail] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [terms, setTerms] = useState("");
  const [project, setProject] = useState("");
  const [currency, setCurrency] = useState("JMD");
  const [lines, setLines] = useState([blankLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/invoices/next-number").then(({ data }) => setNextNumber(data.invoice_number)).catch(() => {});
    apiClient.get("/admin/invoices/suggest-file-reference").then(({ data }) => {
      setLines((ls) => ls.map((l, i) => (i === 0 ? { ...l, file_reference: data.file_reference } : l)));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const handleDescriptionChange = async (key, description) => {
    const patch = { description, rate: DEFAULT_RATES[description] ?? 0, taxable: DEFAULT_TAXABLE[description] ?? false };
    updateLine(key, patch);
    if (description === "Brokerage Fee") {
      try {
        const { data } = await apiClient.get("/admin/invoices/suggest-file-reference");
        updateLine(key, { file_reference: data.file_reference });
      } catch {
        // non-critical convenience suggestion; admin can still type it manually
      }
    }
  };

  const addLine = () => setLines((ls) => [...ls, blankLine()]);
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));

  const handleAssistAction = (actionType, payload) => {
    if (actionType !== "SUGGEST_LINE_ITEMS" || !payload?.items?.length) return;
    const added = payload.items.map((item) => ({
      ...blankLine(),
      description: DESCRIPTION_OPTIONS.includes(item.description) ? item.description : "Custom Entry",
      customDescription: DESCRIPTION_OPTIONS.includes(item.description) ? "" : item.description,
      rate: Number(item.rate) || 0,
      taxable: !!item.taxable,
    }));
    setLines((ls) => [...ls.filter((l) => l.rate > 0 || l.customDescription), ...added]);
    toast.success(`Added ${added.length} suggested line item${added.length === 1 ? "" : "s"} — review before saving`);
  };

  const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.rate, 0);
  const taxable = lines.reduce((sum, l) => sum + (l.taxable ? l.quantity * l.rate : 0), 0);
  const tax = Math.round(taxable * 0.15 * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;

  const resetForm = () => {
    setDate(todayDMY());
    setBillToName("");
    setBillToAddress("");
    setBillToEmail("");
    setPoNumber("");
    setTerms("");
    setProject("");
    setCurrency("JMD");
    setLines([blankLine()]);
  };

  const handleSubmit = async () => {
    if (!billToName.trim() || !billToAddress.trim()) {
      toast.error("Bill To name and address are required");
      return;
    }
    const line_items = lines
      .filter((l) => l.rate > 0 || l.customDescription.trim())
      .map((l) => ({
        description: l.description === "Custom Entry" ? l.customDescription.trim() || "Custom Entry" : l.description,
        quantity: Number(l.quantity) || 1,
        rate: Number(l.rate) || 0,
        taxable: l.taxable,
        ...(l.description === "Brokerage Fee" && l.file_reference ? { file_reference: l.file_reference } : {}),
      }));
    if (line_items.length === 0) {
      toast.error("Add at least one line item with a rate");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post("/admin/invoices", {
        date, bill_to_name: billToName, bill_to_address: billToAddress, bill_to_email: billToEmail || undefined,
        po_number: poNumber, terms, project, currency, line_items,
      });
      toast.success(`Invoice #${data.invoice_number} created`);
      resetForm();
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6" data-testid="invoice-form">
      <div className="rounded-2xl border border-gold/15 bg-deep-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="font-display font-semibold text-[#F4F7F5]">New Invoice</span>
          {nextNumber && <span className="text-xs font-mono text-[#6E7E74]" data-testid="invoice-next-number">Invoice # {nextNumber}</span>}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Date">
            <input value={date} onChange={(e) => setDate(e.target.value)} data-testid="invoice-input-date" className="admin-field" />
          </Field>
          <Field label="Currency">
            <div className="flex gap-2">
              {["JMD", "USD"].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  data-testid={`invoice-currency-${c.toLowerCase()}`}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                    currency === c ? "bg-gold text-[#0A140F]" : "bg-deep border border-gold/15 text-[#A1B0A6]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <Field label="Bill To — Client Name">
          <input value={billToName} onChange={(e) => setBillToName(e.target.value)} data-testid="invoice-input-bill-to-name" className="admin-field" />
        </Field>
        <Field label="Bill To — Address">
          <textarea value={billToAddress} onChange={(e) => setBillToAddress(e.target.value)} rows={2} data-testid="invoice-input-bill-to-address" className="admin-field resize-none" />
        </Field>
        <Field label="Bill To — Email (for sending the invoice)">
          <input type="email" value={billToEmail} onChange={(e) => setBillToEmail(e.target.value)} placeholder="client@example.com" data-testid="invoice-input-bill-to-email" className="admin-field" />
        </Field>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="P.O. No."><input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} data-testid="invoice-input-po" className="admin-field" /></Field>
          <Field label="Terms"><input value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="invoice-input-terms" className="admin-field" /></Field>
          <Field label="Project"><input value={project} onChange={(e) => setProject(e.target.value)} data-testid="invoice-input-project" className="admin-field" /></Field>
        </div>

        <div className="pt-2 border-t border-gold/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#6E7E74] uppercase tracking-wide">Line Items</span>
            <button type="button" onClick={addLine} data-testid="invoice-add-line-btn" className="flex items-center gap-1 text-xs text-gold hover:text-gold-hover">
              <Plus size={13} /> Add Line Item
            </button>
          </div>
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.key} data-testid={`invoice-line-${l.key}`} className="rounded-lg border border-gold/10 bg-deep p-3 grid grid-cols-[1fr_auto] gap-2">
                <div className="space-y-2">
                  <div className="grid sm:grid-cols-[1.4fr_0.6fr_0.8fr] gap-2">
                    <select
                      value={l.description}
                      onChange={(e) => handleDescriptionChange(l.key, e.target.value)}
                      data-testid={`invoice-line-description-${l.key}`}
                      className="admin-field"
                    >
                      {DESCRIPTION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input
                      type="number" step="0.01" value={l.quantity}
                      onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                      data-testid={`invoice-line-quantity-${l.key}`}
                      placeholder="Qty" className="admin-field"
                    />
                    <input
                      type="number" step="0.01" value={l.rate}
                      onChange={(e) => updateLine(l.key, { rate: e.target.value })}
                      data-testid={`invoice-line-rate-${l.key}`}
                      placeholder="Rate" className="admin-field"
                    />
                  </div>
                  {l.description === "Custom Entry" && (
                    <input
                      value={l.customDescription}
                      onChange={(e) => updateLine(l.key, { customDescription: e.target.value })}
                      placeholder="Describe this line item"
                      data-testid={`invoice-line-custom-desc-${l.key}`}
                      className="admin-field w-full"
                    />
                  )}
                  {l.description === "Brokerage Fee" && (
                    <input
                      value={l.file_reference}
                      onChange={(e) => updateLine(l.key, { file_reference: e.target.value })}
                      placeholder="File reference (editable)"
                      data-testid={`invoice-line-file-reference-${l.key}`}
                      className="admin-field w-full font-mono text-xs"
                    />
                  )}
                  <label className="flex items-center gap-2 text-xs text-[#A1B0A6] cursor-pointer w-fit">
                    <input
                      type="checkbox" checked={l.taxable}
                      onChange={(e) => updateLine(l.key, { taxable: e.target.checked })}
                      data-testid={`invoice-line-taxable-${l.key}`}
                      className="accent-gold h-3.5 w-3.5"
                    />
                    Taxable (T)
                  </label>
                </div>
                <button
                  type="button" onClick={() => removeLine(l.key)}
                  data-testid={`invoice-line-remove-${l.key}`}
                  className="text-[#6E7E74] hover:text-red-400 self-start"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-3 border-t border-gold/10 flex flex-col items-end gap-1 text-sm">
          <div className="flex justify-between w-48 text-[#A1B0A6]"><span>Subtotal</span><span>{currency} {subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between w-48 text-[#A1B0A6]"><span>GCT (15%)</span><span>{currency} {tax.toFixed(2)}</span></div>
          <div className="flex justify-between w-48 text-[#F4F7F5] font-display font-bold text-base" data-testid="invoice-total-preview">
            <span>Total</span><span>{currency} {total.toFixed(2)}</span>
          </div>
        </div>

        <button
          onClick={handleSubmit} disabled={saving}
          data-testid="invoice-submit-btn"
          className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] text-sm font-display font-semibold px-5 py-2.5 rounded-md transition-colors"
        >
          {saving ? "Creating..." : "Create Invoice"}
        </button>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2 text-xs text-[#6E7E74] uppercase tracking-wide">
          <Sparkles size={13} className="text-gold" /> Invoice Assist
        </div>
        <AdminAssistantChat
          mode="invoice"
          greeting="Give me a file reference or shipment description and I'll suggest line items for this invoice."
          placeholder="e.g. Brokerage job, declared value 180,000..."
          onAction={handleAssistAction}
        />
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-[#6E7E74] uppercase tracking-wide mb-1 block">{label}</label>
    {children}
  </div>
);
